import React from 'react';
import { ExternalLink, Download } from 'lucide-react';
import { JobsiteService } from '../../services/jobsite.service';
import { toast } from 'react-toastify';

interface FilesSectionProps {
    jobsiteFiles: any[];
    handleViewFile: (fileId: string) => void;
}

const FilesSection: React.FC<FilesSectionProps> = ({ jobsiteFiles, handleViewFile }) => {

    const getMimeType = (fileName: string): string => {
        const extension = fileName.toLowerCase().split('.').pop();
        const mimeTypes: { [key: string]: string } = {
            // Images
            'jpg': 'image/jpeg',
            'jpeg': 'image/jpeg',
            'png': 'image/png',
            'gif': 'image/gif',
            'bmp': 'image/bmp',
            'webp': 'image/webp',
            'svg': 'image/svg+xml',
            
            // Documents
            'pdf': 'application/pdf',
            'doc': 'application/msword',
            'docx': 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
            'xls': 'application/vnd.ms-excel',
            'xlsx': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
            'ppt': 'application/vnd.ms-powerpoint',
            'pptx': 'application/vnd.openxmlformats-officedocument.presentationml.presentation',
            'txt': 'text/plain',
            'csv': 'text/csv',
            
            // Other formats
            'heif': 'image/heif',
            'tiff': 'image/tiff',
            'tif': 'image/tiff'
        };
        
        return mimeTypes[extension || ''] || 'application/octet-stream';
    };
    const handlePreviewFile = async (attachmentId: string, fileName: string) => {
        try {
            // Fetch file using attachment ID (secure approach)
            const blobData = await JobsiteService.viewFile(attachmentId) as Blob;

            // Create a new blob with the correct MIME type
            const mimeType = getMimeType(fileName);
            const blob = new Blob([blobData], { type: mimeType });

            const url = window.URL.createObjectURL(blob);

            // Open in new tab
            const newTab = window.open(url, '_blank');

            // Clean up the URL after a delay to allow the new tab to load
            setTimeout(() => {
                URL.revokeObjectURL(url);
            }, 1000);

            if (!newTab) {
                toast.error('Please allow popups for this website to preview files');
            }
        } catch (error) {
            console.error('Preview failed', error);
            toast.error('Failed to preview file');
        }
    };

    const handleDownloadFile = async (attachmentId: string, fileName: string) => {
        try {
            // Fetch file using attachment ID (secure approach)
            const blobData = await JobsiteService.viewFile(attachmentId) as Blob;

            // Create a new blob with the correct MIME type
            const mimeType = getMimeType(fileName);
            const blob = new Blob([blobData], { type: mimeType });

            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = fileName;
            document.body.appendChild(a);
            a.click();
            a.remove();
            URL.revokeObjectURL(url);
        } catch (error) {
            console.error('Download failed', error);
            toast.error('Failed to download file');
        }
    };


    const formatDate = (date: string) => {
        return new Date(date).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'long',
            day: 'numeric'
        });
    };
    return (
        <div className='bg-white mb-4'>
            <div className='p-4 sm:p-6'>
                <h2 className='text-[20px] font-[700] mb-3'>Files</h2>
                {jobsiteFiles.length === 0 ? (
                    <p className='text-gray-500'>No files uploaded for this jobsite.</p>
                ) : (
                    <div className="space-y-2">
                        {jobsiteFiles.map((file) => (
                            <div key={file.id} className="flex items-center">
                               <div className="flex items-center">
                                    <span className="text-sm text-gray-600 mr-2">•</span>
                                    <span className="text-[16px] text-gray-600">{file.fileName || '-'} - {formatDate(file.createdAt)}</span>
                                     <div className="flex items-center gap-1 pl-2">
                                    <button
                                        onClick={() => handlePreviewFile(file.attachment.id, file.fileName)}
                                        className="text-gray-400 hover:text-blue-600 transition-colors p-1 rounded-full hover:bg-gray-100"
                                        title="Preview file in new tab"
                                    >
                                        <ExternalLink className="w-4 h-4" />
                                    </button>
                                    <button
                                        onClick={() => handleDownloadFile(file.attachment.id, file.fileName)}
                                        className="text-gray-400 hover:text-blue-600 transition-colors p-1 rounded-full hover:bg-gray-100"
                                        title="Download file"
                                    >
                                        <Download className="w-4 h-4" />
                                    </button>
                                </div>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
};

export default FilesSection; 